import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Button from '../../Button';
import ErrorBlock from '../../common/ErrorBlock';
import { AUTH, PROFILE } from '../../../constants/strings';
import PasswordInput from '../../TextInput/PasswordInput';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { useSimpleForm } from '../../common/SimpleFormPopup/utils/useSimpleForm';
import styles from '../index.styles';
import { enhance } from './enhance';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';
import CheckingListTooltip from '../../CheckPasswordList';
import {
  getUsernameTooltipOptions,
  validateUsernameCharacters,
  validateUsernameLength,
  validateUsernameRepeating,
  validateUsernameSpaces,
} from '../utils/usernameValidation';
import Icon from '../../Icon';
import TextInput from '../../TextInput';
import { selectUsernameAvailabilityError } from '../../../redux/auth/signup/selectors';

const FIELDS = [
  PROFILE_FIELDS_TYPES.USERNAME,
  PROFILE_FIELDS_TYPES.PASSWORD,
  PROFILE_FIELDS_TYPES.PASSWORD_CONFIRMATION,
];

const ChangePassword = ({
  errors,
  proceed,
  intl: { formatMessage },
  changePasswordFormData,
}) => {
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [isUsernameTouched, setIsUsernameTouched] = useState(false);
  const {
    handleFormChange,
    validateErrors,
    clearErrors,
    formState,
    isValid,
  } = useSimpleForm(FIELDS, FIELDS, errors, changePasswordFormData);
  const [disabled, setDisabledState] = useState(!changePasswordFormData);
  usePageView('Change Password');
  const passwordError = get(validateErrors, 'password');

  const usernameAvailabilityError = useSelector(
    selectUsernameAvailabilityError,
  );

  const { username, password, passwordConfirmation } = formState;
  const isValidPasswordsMatch = password === passwordConfirmation;

  const isValidUsername =
    validateUsernameLength(username) &&
    validateUsernameCharacters(username) &&
    validateUsernameSpaces(username) &&
    validateUsernameRepeating(username);

  const availabilityError =
    get(usernameAvailabilityError, 'description', null) &&
    formatMessage(PROFILE.errors.username.taken);

  const isEnabled = isValidPasswordsMatch && !passwordError && isValidUsername;

  const handleSubmit = e => {
    e.preventDefault();
    clearErrors();
    proceed({ ...formState });
  };

  const handlePasswordChange = (name, value, invalid) => {
    handleFormChange({ [name]: value });
    setDisabledState(invalid);
  };

  const handleUsernameChange = value => {
    handleFormChange({ username: value });
  };

  const usernameRequiredError =
    isUsernameTouched && !username
      ? formatMessage(PROFILE.errors.username.required)
      : null;

  const handleOpenPopover = () => setIsOpenPopover(true);
  const handleClosePopover = () => {
    setIsOpenPopover(false);
    setIsUsernameTouched(true);
  };

  return (
    <div className={styles.formWrapper}>
      <MetaHelmet title="pages.title.changePassword" />

      <p className={styles.formTitle}>{formatMessage(AUTH.titles.signup)}</p>
      <p className={styles.formHeading}>
        {formatMessage({ id: 'auth.changePassword.text' })}
      </p>
      <TextInput
        className="mb3 relative"
        id="username"
        label={
          <div className="df items-end">
            <FormattedMessage {...AUTH.fields.username} />
          </div>
        }
        name="username"
        value={username}
        placeholder={formatMessage({ ...AUTH.fields.username })}
        rounded
        type="text"
        invalid={!!usernameRequiredError}
        onChange={handleUsernameChange}
        onFocus={handleOpenPopover}
        onBlur={handleClosePopover}
      >
        {isOpenPopover && (
          <CheckingListTooltip
            title="auth.signup.popover.title"
            list={getUsernameTooltipOptions(username)}
          >
            <div className={styles.popoverInfo}>
              <Icon name="icon-info" size={16} />
              {formatMessage({ id: 'auth.signup.text.username' })}
            </div>
          </CheckingListTooltip>
        )}
      </TextInput>
      {usernameRequiredError && (
        <ErrorBlock
          className={cn(styles.errorBlock, 'mt1 mb2')}
          errors={usernameRequiredError}
        />
      )}
      <PasswordInput
        autoComplete="new-password"
        className={cn(styles.labelField, styles.inputField, 'mb3')}
        name="password"
        label={formatMessage({ id: 'auth.signup.newPassword' })}
        placeholder={formatMessage({ id: 'fields.password' })}
        rounded
        onChange={handlePasswordChange}
        required
        withValidation
        value={password}
      />
      <PasswordInput
        autoComplete="new-password"
        className={cn(styles.labelField, styles.inputField)}
        name="passwordConfirmation"
        rounded
        label={formatMessage(AUTH.actions.signup.confirmationPassword)}
        placeholder={formatMessage({ id: 'fields.password' })}
        onChange={handlePasswordChange}
        required
        withValidation
        value={passwordConfirmation}
      />

      {(!isValidPasswordsMatch || passwordError || availabilityError) && (
        <ErrorBlock
          className={cn(styles.errorBlock, 'mt3')}
          errors={
            availabilityError ||
            passwordError ||
            formatMessage(AUTH.actions.signup.matchPassword)
          }
        />
      )}

      <Button
        className={cn(styles.acceptBtn, 'db mb3 w-100 lh-copy')}
        color="primary"
        raised
        type="submit"
        disabled={!isValid || disabled || !isEnabled}
        onClick={handleSubmit}
      >
        {formatMessage({ id: 'common.next' })}
      </Button>
    </div>
  );
};

ChangePassword.propTypes = {
  errors: PropTypes.object,
  proceed: PropTypes.func.isRequired,
  changePasswordFormData: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
};

export default enhance(ChangePassword);



// WEBPACK FOOTER //
// ./src/components/Auth/ChangePassword/index.jsx